<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.family_interviews', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <UnsavedChangesFormWarning :is-dirty="form.isDirty" v-if="form.isDirty" />
        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    <InertiaLink :href="meta.links.student_show">
                        <img
                            :src="student.avatar_or_default"
                            class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                        />
                    </InertiaLink>

                    <div class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                        <div class="flex flex-col justify-center items-center">
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                <InertiaLink :href="meta.links.student_show">
                                    {{ student.name_or_nickname }}
                                </InertiaLink>
                            </h3>
                            <h3 class="leading-6 font-base text-gray-600">
                                {{ $t('messages.student') }}
                            </h3>
                        </div>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date')" for="date" />
                            <FormInput
                                required
                                id="date"
                                v-model="form.date"
                                name="date"
                                type="date"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.duration')" for="duration" />
                            <FormInput
                                required
                                id="duration"
                                v-model="form.duration"
                                name="duration"
                                type="time"
                                class="mt-1"
                                step="1"
                            />
                            <FormError :value="form.errors.duration" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.type')" for="type_id" />
                            <FormSelect
                                required
                                id="type_id"
                                v-model="form.type_id"
                                :options="options.types"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.type_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.participants')" for="participants" />
                            <FormTextarea
                                rows="3"
                                id="participants"
                                v-model="form.participants"
                                name="participants"
                                type="text"
                                class="mt-1 w-full"
                                style="min-height: 60px"
                            />
                            <FormError :value="form.errors.participants" class="mt-2" />
                            <div v-auto-animate>
                                <AppWell class="mt-1" v-if="nonSelectedRelatives.length">
                                    <div v-auto-animate>
                                        <div v-for="participant in nonSelectedRelatives" :key="participant.id">
                                            <div
                                                class="flex space-x-2 cursor-pointer"
                                                @click="addParticipant(participant)"
                                                v-show="!participant.selected"
                                            >
                                                <div>
                                                    <svg
                                                        class="w-6 h-6 text-gray-400"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </div>
                                                <span class="text-sm"> {{ participant.text }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </AppWell>
                            </div>
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.requested_by')" for="requested_by" />
                            <FormInput
                                id="requested_by"
                                v-model="form.requested_by"
                                name="requested_by"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.requested_by" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.reason')" for="reason" />
                            <FormInput
                                id="reason"
                                v-model="form.reason"
                                name="reason"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.reason" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.topics_covered')" for="topics_covered" />
                            <FormTextarea
                                rows="3"
                                id="topics_covered"
                                v-model="form.topics_covered"
                                name="topics_covered"
                                type="text"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.topics_covered" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.agreements_purposes')" for="agreements_purposes" />
                            <FormTextarea
                                rows="3"
                                id="agreements_purposes"
                                v-model="form.agreements_purposes"
                                name="agreements_purposes"
                                type="text"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.agreements_purposes" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.links', 2)" for="links" />
                            <LinkBatchCreate v-model="form.links" :links-input="[]" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import AppButton from '@/components/AppButton.vue';
import AppWell from '@/Shared/AppWell.vue';
import LinkBatchCreate from '@/components/pageSpecific/LinkBatchCreate.vue';
import UnsavedChangesFormWarning from '@/components/Forms/UnsavedChangesFormWarning.vue';

export default {
    components: {
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        AppButton,
        AppWell,
        LinkBatchCreate,
        UnsavedChangesFormWarning,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options', 'student', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                date: this.defaults.date,
                topics_covered: '',
                agreements_purposes: '',
                participants: this.defaults.participants,
                requested_by: this.defaults.requested_by,
                duration: this.defaults.duration,
                student_id: this.defaults.student_id,
                reason: '',
                type_id: '',
                links: [],
            }),
            relatives: this.defaults.relatives,
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        addParticipant(participant) {
            participant.selected = true;
            this.form.participants = this.form.participants + '\n' + participant.text;
        },
    },
    computed: {
        nonSelectedRelatives() {
            return this.relatives.filter((p) => !p.selected);
        },
    },
};
</script>
